import type { Matter } from "@/shared/cache";

export function getRecentMatters(matters: Matter[]): Matter[] {
  return matters
    .filter((m) => m.recency_rank != null)
    .sort((a, b) => (a.recency_rank ?? 0) - (b.recency_rank ?? 0));
}

function scoreRelevance(m: Matter, q: string): number {
  const desc = m.description.toLowerCase();
  const client = m.client.toLowerCase();
  const matter = m.matter.toLowerCase();
  if (desc.startsWith(q)) return 3;
  if (client.startsWith(q)) return 2;
  if (matter.startsWith(q)) return 2;
  return 1;
}

// 0 = letter, 1 = digit, 2 = other — used to bucket the alpha tiebreaker
function startCategory(s: string): 0 | 1 | 2 {
  const c = s.charCodeAt(0);
  if ((c >= 65 && c <= 90) || (c >= 97 && c <= 122)) return 0;
  if (c >= 48 && c <= 57) return 1;
  return 2;
}

export function searchMatters(
  matters: Matter[],
  query: string,
  caseListIds?: Set<string>,
): Matter[] {
  const q = query.toLowerCase().trim();
  const tokens = q.split(/\s+/);
  const inCaseList = (m: Matter): 0 | 1 =>
    caseListIds && caseListIds.has(m.matter_id) ? 1 : 0;
  return matters
    .filter((m) => {
      const desc = m.description.toLowerCase();
      const client = m.client.toLowerCase();
      const matter = m.matter.toLowerCase();
      return tokens.every(
        (t) => desc.includes(t) || client.includes(t) || matter.includes(t),
      );
    })
    .map((m) => ({ m, score: scoreRelevance(m, q) }))
    .sort((a, b) => {
      if (a.score !== b.score) return b.score - a.score;
      // Within the same score tier, case-list matters come first.
      const aCL = inCaseList(a.m);
      const bCL = inCaseList(b.m);
      if (aCL !== bCL) return bCL - aCL;
      const aCat = startCategory(a.m.description);
      const bCat = startCategory(b.m.description);
      if (aCat !== bCat) return aCat - bCat;
      if (aCat === 2) {
        return a.m.description < b.m.description ? -1 : a.m.description > b.m.description ? 1 : 0;
      }
      return a.m.description.localeCompare(b.m.description);
    })
    .map((x) => x.m);
}
